import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import type { KarmaCircle, KarmaUser } from "@/lib/karma/types";
import { SupabaseAuthCard } from "@/components/supabase";
import { useSupabaseSession } from "@/hooks/use-supabase-session";
import {
  ensureCircleAndMembership,
  fetchCircleMessageById,
  listCircleMessages,
  sendCircleMessage,
  subscribeCircleMessages,
  type CircleMessage,
} from "@/lib/supabase/circles";

function formatTime(iso: string) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

export function CircleChat({ circle, me }: { circle: KarmaCircle; me: KarmaUser }) {
  const { user, loading, error } = useSupabaseSession();

  const [ready, setReady] = useState(false);
  const [messages, setMessages] = useState<CircleMessage[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const listRef = useRef<HTMLDivElement | null>(null);

  const myUserId = user?.id ?? "";

  const canChat = Boolean(!loading && user && !error);

  const title = useMemo(() => `${circle.name} • Grup sohbeti`, [circle.name]);

  const scrollToBottom = () => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    if (!canChat) {
      setReady(false);
      setMessages([]);
      return;
    }

    let unsub: null | (() => void) = null;
    let cancelled = false;

    const boot = async () => {
      setChatError(null);
      setReady(false);
      try {
        await ensureCircleAndMembership({
          circleId: circle.id,
          me: { displayName: me.displayName, handle: me.handle },
        });
        const initial = await listCircleMessages({ circleId: circle.id, limit: 80 });
        if (cancelled) return;
        setMessages(initial);
        setReady(true);
        queueMicrotask(scrollToBottom);

        unsub = subscribeCircleMessages({
          circleId: circle.id,
          onInserted: async (id) => {
            try {
              const msg = await fetchCircleMessageById(id);
              if (!msg) return;
              setMessages((prev) => {
                if (prev.some((m) => m.id === msg.id)) return prev;
                return [...prev, msg].slice(-200);
              });
              queueMicrotask(scrollToBottom);
            } catch {
              // ignore
            }
          },
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Sohbet başlatılamadı";
        if (!cancelled) setChatError(msg);
      }
    };

    void boot();

    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [canChat, circle.id, circle.name, me.id, me.displayName, me.handle]);

  const onSend = async () => {
    const body = text.trim();
    if (!body) return;
    setBusy(true);
    try {
      await sendCircleMessage({ circleId: circle.id, body });
      setText("");

      // Realtime gecikirse de gönderen taraf mesajı hemen görsün.
      const fresh = await listCircleMessages({ circleId: circle.id, limit: 80 });
      setMessages(fresh);
      queueMicrotask(scrollToBottom);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Mesaj gönderilemedi";
      setChatError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-2xl border bg-muted/20 p-4 text-sm text-muted-foreground">
        Supabase: {error}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground">
          Grup sohbeti için giriş gerekli (mesajlar güvenli şekilde üyelerle paylaşılır).
        </div>
        <SupabaseAuthCard me={me} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold">{title}</div>
        <div className="mt-1 text-sm text-muted-foreground">
          {ready ? "Canlı" : "Hazırlanıyor"}
          {chatError ? ` • ${chatError}` : ""}
        </div>
      </div>

      <div
        ref={listRef}
        className="h-[320px] overflow-y-auto rounded-2xl border bg-muted/20 p-3"
      >
        {messages.length ? (
          <div className="space-y-2">
            {messages.map((m) => {
              const mine = m.userId === myUserId;
              return (
                <div
                  key={m.id}
                  className={cn("flex", mine ? "justify-end" : "justify-start")}
                >
                  <div
                    className={cn(
                      "max-w-[85%] rounded-2xl px-4 py-2 text-sm",
                      mine
                        ? "bg-primary text-primary-foreground"
                        : "bg-background text-foreground border",
                    )}
                  >
                    <div className={cn("text-[11px] opacity-80", mine ? "text-primary-foreground" : "text-muted-foreground")}>
                      {mine ? "Sen" : m.senderName} • {formatTime(m.createdAt)}
                    </div>
                    <div className="mt-1 whitespace-pre-wrap break-words">{m.body}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">Henüz mesaj yok. İlk mesajı sen yaz.</div>
        )}
      </div>

      <Separator />

      <div className="flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Mesaj yaz…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void onSend();
            }
          }}
        />


        <Button className="shrink-0 rounded-full" onClick={onSend} disabled={busy}>
          Gönder
        </Button>
      </div>
    </div>
  );
}
